import type {
  ProjectContextComposeLearnedItem,
  ProjectContextTargetLang,
  ProjectContextTerm,
} from '@ai-meeting/shared'

/** Dedup key matching BE normalize: NFKC + lowercase(source) + targetLang. */
export function projectDictionaryTermKey(source: string, targetLang: string): string {
  return `${source.normalize('NFKC').toLowerCase()}\0${targetLang}`
}

/**
 * Apply Block to a learned term on the declared half.
 *
 * Same `(source, targetLang)` can already hold a manual ACTIVE entry. Blocking
 * must keep that entry's `target` (and replace status with BLOCKED), not wipe
 * the declared translation.
 */
export function blockLearnedProjectTerm(
  terms: ProjectContextTerm[],
  learned: ProjectContextComposeLearnedItem
): ProjectContextTerm[] {
  const key = projectDictionaryTermKey(learned.source, learned.targetLang)
  const existing = terms.find(
    (term) => projectDictionaryTermKey(term.source, term.targetLang) === key
  )
  const target = existing?.target ?? (learned.target ? learned.target : undefined)
  const blocked: ProjectContextTerm = target
    ? {
        source: existing?.source ?? learned.source,
        target,
        targetLang: learned.targetLang,
        status: 'BLOCKED',
      }
    : {
        source: existing?.source ?? learned.source,
        targetLang: learned.targetLang,
        status: 'BLOCKED',
      }

  return [
    ...terms.filter(
      (term) => projectDictionaryTermKey(term.source, term.targetLang) !== key
    ),
    blocked,
  ]
}

export function unblockLearnedProjectTerm(
  terms: ProjectContextTerm[],
  learned: Pick<ProjectContextComposeLearnedItem, 'source' | 'targetLang'>
): ProjectContextTerm[] {
  return terms.filter(
    (term) =>
      !(
        term.status === 'BLOCKED' &&
        projectDictionaryTermKey(term.source, term.targetLang) ===
          projectDictionaryTermKey(learned.source, learned.targetLang)
      )
  )
}

export function isProjectTermBlocked(
  terms: ProjectContextTerm[],
  source: string,
  targetLang: ProjectContextTargetLang
): boolean {
  return terms.some(
    (t) =>
      t.status === 'BLOCKED' &&
      projectDictionaryTermKey(t.source, t.targetLang) ===
        projectDictionaryTermKey(source, targetLang)
  )
}
